import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Phone, Mail, MoreHorizontal, Pencil, Power, Trash2, X, Users, Camera } from 'lucide-react';
import ApiServices from '../services/ApiServices';
import { ShowDangerToast, ShowSuccessToast } from '../services/toast';

const COLOR_PALETTE = [
  '#4CAF50', '#2196F3', '#3F51B5', '#9C27B0', '#FF9800',
  '#E91E63', '#F44336', '#FF5722', '#00BCD4', '#8BC34A',
  '#CDDC39', '#FFC107', '#795548', '#607D8B',
];

const Drivers = ({ storeId }) => {
  const [drivers, setDrivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);
  const [driverForm, setDriverForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    password: '',
    maxPickupsPerDay: 10,
    color: '#4CAF50',
    isActive: true,
    profilePicture: null,
  });
  const [phoneError, setPhoneError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDrivers();
  }, [storeId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchDrivers = async () => {
    try {
      const response = await ApiServices.getData(`/drivers?storeId=${storeId}`);
      setDrivers(response.data || []);
    } catch {
      ShowDangerToast('Failed to load drivers');
    }
  };

  const validatePhoneNumber = (phoneNumber) => {
    const cleanedNumber = phoneNumber.replace(/[\s()-]/g, '');
    const indianNumberRegex = /^\+91[6-9][0-9]{9}$/;
    const usaNumberRegex = /^\+1[0-9]{10}$/;
    return indianNumberRegex.test(cleanedNumber) || usaNumberRegex.test(cleanedNumber);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === 'phone') {
      if (value && !value.startsWith('+') && /^\d/.test(value)) {
        newValue = '+1' + value;
      }
      if (newValue && !validatePhoneNumber(newValue)) {
        setPhoneError('Phone number must be 10 digits after country code');
      } else {
        setPhoneError('');
      }
    }
    setDriverForm({ ...driverForm, [name]: newValue });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDriverForm({ ...driverForm, profilePicture: file });
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const resetForm = () => {
    setDriverForm({
      fullName: '',
      phone: '',
      email: '',
      password: '',
      maxPickupsPerDay: 10,
      color: '#4CAF50',
      isActive: true,
      profilePicture: null,
    });
    setPreviewUrl(null);
    setPhoneError('');
    setIsEditing(false);
    setEditingDriverId(null);
    setShowModal(false);
  };

  const handleDriverSubmit = async (e) => {
    e.preventDefault();
    if (!validatePhoneNumber(driverForm.phone)) {
      ShowDangerToast('Invalid phone number. Use +1XXXXXXXXXX or +91XXXXXXXXXX');
      return;
    }
    if (!isEditing && !driverForm.password) {
      ShowDangerToast('Password is required for new drivers');
      return;
    }

    const formData = new FormData();
    formData.append('fullName', driverForm.fullName);
    formData.append('phone', driverForm.phone);
    formData.append('email', driverForm.email);
    formData.append('maxPickupsPerDay', driverForm.maxPickupsPerDay);
    formData.append('color', driverForm.color);
    formData.append('isActive', driverForm.isActive);
    if (driverForm.password) formData.append('password', driverForm.password);
    if (driverForm.profilePicture) formData.append('profilePicture', driverForm.profilePicture);
    if (!isEditing) formData.append('storeId', storeId);

    try {
      if (isEditing) {
        await ApiServices.putFormData(`/drivers/${editingDriverId}`, formData);
        ShowSuccessToast('Driver updated successfully');
      } else {
        await ApiServices.postFormData('/drivers', formData);
        ShowSuccessToast('Driver added successfully');
      }
      await fetchDrivers();
      resetForm();
    } catch (error) {
      ShowDangerToast(isEditing ? 'Failed to update driver' : 'Failed to add driver');
    }
  };

  const handleEditDriver = (driver) => {
    setIsEditing(true);
    setEditingDriverId(driver.id);
    setDriverForm({
      fullName: driver.fullName,
      phone: driver.phone,
      email: driver.email || '',
      password: '',
      maxPickupsPerDay: driver.maxPickupsPerDay || 10,
      color: driver.color || '#4CAF50',
      isActive: driver.isActive !== false,
      profilePicture: null,
    });
    setPreviewUrl(driver.profilepicture || null);
    setPhoneError('');
    setOpenMenuId(null);
    setShowModal(true);
  };

  const handleToggleActive = async (driver) => {
    const action = driver.isActive ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${driver.fullName}?`)) return;
    try {
      await ApiServices.putData(`/drivers/${driver.id}`, { isActive: !driver.isActive });
      ShowSuccessToast(`Driver ${driver.isActive ? 'deactivated' : 'activated'} successfully`);
      await fetchDrivers();
    } catch {
      ShowDangerToast('Failed to update driver status');
    }
    setOpenMenuId(null);
  };

  const handleDriverDelete = async (driverId) => {
    const driver = drivers.find(d => d.id === driverId);
    if (!window.confirm(`Are you sure you want to delete ${driver?.fullName || 'this driver'}? This action cannot be undone.`)) return;
    try {
      await ApiServices.deleteData(`/drivers/delete/${driverId}`);
      ShowSuccessToast('Driver deleted successfully');
      setDrivers(drivers.filter((d) => d.id !== driverId));
    } catch {
      ShowDangerToast('Failed to delete driver');
    }
    setOpenMenuId(null);
  };

  const activeDrivers = drivers.filter((d) => d && d.isActive !== false);
  const filteredDrivers = drivers.filter(
    (driver) =>
      driver &&
      (driver.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        driver.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const getInitial = (name) => (name ? name.charAt(0).toUpperCase() : '?');

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Drivers</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {activeDrivers.length} active driver{activeDrivers.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Driver
        </button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search drivers..."
          className="input-field pl-10 w-full"
        />
      </div>

      {filteredDrivers.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">No drivers found.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDrivers.map((driver) =>
          !driver || typeof driver !== 'object' ? null : (
            <div
              key={driver.id}
              className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow relative"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  {driver.profilepicture && driver.profilepicture.trim() !== '' ? (
                    <img
                      src={driver.profilepicture}
                      alt={driver.fullName}
                      className="w-10 h-10 rounded-full object-cover border border-gray-200"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-lg flex-shrink-0"
                      style={{ backgroundColor: driver.color || '#4CAF50' }}
                    >
                      {getInitial(driver.fullName)}
                    </div>
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">{driver.fullName}</h4>
                    <span
                      className={`inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-medium ${driver.isActive !== false
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                        }`}
                    >
                      {driver.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="relative" ref={openMenuId === driver.id ? menuRef : null}>
                  <button
                    onClick={() => setOpenMenuId(openMenuId === driver.id ? null : driver.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </button>
                  {openMenuId === driver.id && (
                    <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[140px]">
                      <button
                        onClick={() => handleEditDriver(driver)}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(driver)}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Power className="w-4 h-4" />
                        {driver.isActive !== false ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDriverDelete(driver.id)}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-1.5 mb-4">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Phone className="w-3.5 h-3.5" />
                  <span className="truncate">{driver.phone}</span>
                </div>
                {driver.email && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Mail className="w-3.5 h-3.5" />
                    <span className="truncate">{driver.email}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-gray-100 pt-3">
                <div className="text-center flex-1">
                  <p className="text-lg font-bold text-gray-900">{driver.todayPickups || 0}</p>
                  <p className="text-xs text-gray-400">Today</p>
                </div>
                <div className="text-center flex-1">
                  <p className="text-lg font-bold text-gray-900">{driver.completedPickups || 0}</p>
                  <p className="text-xs text-gray-400">Completed</p>
                </div>
                <div className="text-center flex-1">
                  <p className="text-lg font-bold text-gray-900">{driver.maxPickupsPerDay || 10}</p>
                  <p className="text-xs text-gray-400">Daily Limit</p>
                </div>
              </div>
            </div>
          )
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={resetForm}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 mb-5 pr-6">
              {isEditing ? 'Edit Driver' : 'Add Driver'}
            </h3>
            <form onSubmit={handleDriverSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Profile Picture</label>
                <div className="flex items-center gap-4">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview" className="w-16 h-16 rounded-full object-cover border border-gray-200" />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                      <Camera className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                  >
                    Upload
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                <input
                  className="input-field w-full"
                  type="text"
                  name="fullName"
                  placeholder="John Smith"
                  required
                  value={driverForm.fullName}
                  onChange={handleFormChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  className="input-field w-full"
                  type="tel"
                  name="phone"
                  placeholder="+1 555 123 4567"
                  required
                  value={driverForm.phone}
                  onChange={handleFormChange}
                />
                {phoneError && <p className="text-red-500 text-xs mt-1">{phoneError}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  className="input-field w-full"
                  type="email"
                  name="email"
                  placeholder="john@example.com"
                  value={driverForm.email}
                  onChange={handleFormChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password {!isEditing && '*'}</label>
                <input
                  className="input-field w-full"
                  type="password"
                  name="password"
                  placeholder={isEditing ? 'Leave blank to keep current' : 'Create password'}
                  value={driverForm.password}
                  onChange={handleFormChange}
                  required={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Pickups Per Day</label>
                <input
                  className="input-field w-full"
                  type="number"
                  name="maxPickupsPerDay"
                  min="1"
                  value={driverForm.maxPickupsPerDay}
                  onChange={handleFormChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_PALETTE.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setDriverForm({ ...driverForm, color: c })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${driverForm.color === c ? 'border-gray-800 scale-110' : 'border-transparent'
                        }`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Active</label>
                <button
                  type="button"
                  onClick={() => setDriverForm({ ...driverForm, isActive: !driverForm.isActive })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${driverForm.isActive ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${driverForm.isActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-5 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                >
                  {isEditing ? 'Save Changes' : 'Add Driver'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Drivers;